import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO, breadcrumb } from "@/lib/seo";
import { trackReserveClick } from "@/lib/analytics";
import { ArrowRight, ChevronLeft } from "lucide-react";

// Renders any SEO page (slug matched by App.js)
export default function SeoPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    setPage(null); setNotFound(false);
    api.get(`/seo-pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useSEO(page ? {
    title: page.title,
    description: page.meta_description,
    image: page.cover_image,
    keywords: page.keywords,
    canonical: page.canonical || (typeof window !== "undefined" ? window.location.href : undefined),
    jsonLd: [
      breadcrumb([
        { name: "Accueil", url: window.location.origin + "/" },
        { name: page.title, url: window.location.href },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": page.title,
        "description": page.meta_description,
        "image": page.cover_image,
        "datePublished": page.updated_at,
        "dateModified": page.updated_at,
        "author": { "@type": "Organization", "name": "L'Entracte" },
        "publisher": { "@type": "Organization", "name": "L'Entracte" },
      },
    ],
  } : { title: "Chargement…" });

  if (notFound) {
    return (
      <div data-testid="seo-not-found" className="px-6 md:px-12 lg:px-16 py-32 max-w-[1500px] mx-auto">
        <p className="overline">404</p>
        <h1 className="font-display text-5xl mt-4 text-[#203240]">Page introuvable</h1>
        <button onClick={() => nav("/")} className="btn-ghost mt-8">Retour à l'accueil</button>
      </div>
    );
  }
  if (!page) {
    return <div className="px-6 py-32 text-[#697585] text-center">Chargement…</div>;
  }

  return (
    <div data-testid="seo-page">
      {/* HERO */}
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden -mt-[148px]">
        <img src={page.cover_image} alt={page.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#F6F0E7]" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col justify-end pb-16">
          <Link to="/" className="overline text-[#E6CFA1] inline-flex items-center gap-2 hover:opacity-80"><ChevronLeft size={14}/> Accueil</Link>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl mt-6 text-white font-light leading-tight max-w-4xl">
            {page.h1 || page.title}
          </h1>
        </div>
      </section>

      <article className="px-6 md:px-12 lg:px-16 py-20 max-w-3xl mx-auto">
        {page.intro && (
          <p className="text-lg md:text-xl text-[#3D4A5A] leading-relaxed border-l-2 border-[#B8956A] pl-6">
            {page.intro}
          </p>
        )}
        <SeoContent content={page.content} />

        <div className="mt-16 border-t border-[#D9D3C5] pt-12 flex flex-col sm:flex-row gap-4">
          <Link to="/reservation" onClick={() => trackReserveClick("seo_page")} className="btn-primary">Réserver une table <ArrowRight size={14}/></Link>
          <Link to="/evenements" className="btn-ghost">Demander un devis</Link>
        </div>
      </article>
    </div>
  );
}

// Minimal markdown-ish renderer (## headings, **bold**, lists, paragraphs)
function SeoContent({ content }) {
  if (!content) return null;
  const blocks = content.split(/\n\n+/);
  return (
    <div className="mt-12 space-y-7 text-[#3D4A5A] text-base md:text-lg leading-relaxed">
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        if (trimmed.startsWith("### ")) {
          return <h3 key={i} className="font-display text-2xl md:text-3xl text-[#B8956A] mt-8">{trimmed.slice(4)}</h3>;
        }
        if (trimmed.startsWith("## ")) {
          return <h2 key={i} className="font-display text-3xl md:text-4xl text-[#203240] mt-12 font-light">{trimmed.slice(3)}</h2>;
        }
        if (trimmed.startsWith("# ")) {
          return <h2 key={i} className="font-display text-4xl text-[#203240] mt-12 font-light">{trimmed.slice(2)}</h2>;
        }
        if (trimmed.startsWith("- ")) {
          const items = trimmed.split("\n").filter((l) => l.startsWith("- "));
          return (
            <ul key={i} className="list-none space-y-3 pl-0">
              {items.map((it, j) => (
                <li key={j} className="flex gap-3">
                  <span className="text-[#B8956A] mt-2">·</span>
                  <span dangerouslySetInnerHTML={{ __html: renderInline(it.slice(2)) }} />
                </li>
              ))}
            </ul>
          );
        }
        return <p key={i} dangerouslySetInnerHTML={{ __html: renderInline(trimmed) }} />;
      })}
    </div>
  );
}

function renderInline(s) {
  // **bold**, simple links not needed for now
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#203240] font-semibold">$1</strong>');
}
